import React, { createContext, useReducer } from 'react';
import type { ReactNode } from 'react';
import type { VehicleDerivative } from '../types/manualsearch.types';
import type { Vehicle } from '../types/vinvrmsearch.types';

interface VehicleSearchState {
    selectedVehicle: Vehicle | VehicleDerivative | null;
    favorites: Array<Vehicle | VehicleDerivative>;
    recentSearches: string[];
    activeSearchMethod: 'manual' | 'vinvrm' | null;
}

interface VehicleSearchContextType extends VehicleSearchState {
    selectVehicle: (vehicle: Vehicle | VehicleDerivative) => void;
    toggleFavorite: (vehicle: Vehicle | VehicleDerivative) => void;
    clearSelection: () => void;
    removeFavorite: (vehicleId: string) => void;
    addRecentSearch: (query: string) => void;
    setActiveSearchMethod: (method: 'manual' | 'vinvrm') => void;
}

type VehicleSearchAction =
    | { type: 'SELECT_VEHICLE'; payload: Vehicle | VehicleDerivative }
    | { type: 'TOGGLE_FAVORITE'; payload: Vehicle | VehicleDerivative }
    | { type: 'CLEAR_SELECTION' }
    | { type: 'REMOVE_FAVORITE'; payload: string }
    | { type: 'ADD_RECENT_SEARCH'; payload: string }
    | { type: 'SET_ACTIVE_SEARCH_METHOD'; payload: 'manual' | 'vinvrm' };

const initialState: VehicleSearchState = {
    selectedVehicle: null,
    favorites: [],
    recentSearches: [],
    activeSearchMethod: null,
};

const vehicleSearchReducer = (
    state: VehicleSearchState,
    action: VehicleSearchAction
): VehicleSearchState => {
    switch (action.type) {
        case 'SELECT_VEHICLE':
            return {
                ...state,
                selectedVehicle: action.payload,
            };

        case 'TOGGLE_FAVORITE': {
            const vehicleId = action.payload.id;
            const isCurrentlyFavorite = state.favorites.some((fav) => fav.id === vehicleId);

            return {
                ...state,
                favorites: isCurrentlyFavorite
                    ? state.favorites.filter((fav) => fav.id !== vehicleId)
                    : [...state.favorites, action.payload],
            };
        }

        case 'CLEAR_SELECTION':
            return {
                ...state,
                selectedVehicle: null,
            };

        case 'REMOVE_FAVORITE':
            return {
                ...state,
                favorites: state.favorites.filter((fav) => fav.id !== action.payload),
            };

        case 'ADD_RECENT_SEARCH': {
            const newRecentSearches = [
                action.payload,
                ...state.recentSearches.filter((search) => search !== action.payload),
            ].slice(0, 5); // Keep last 5 recent searches

            return {
                ...state,
                recentSearches: newRecentSearches,
            };
        }

        case 'SET_ACTIVE_SEARCH_METHOD':
            return {
                ...state,
                activeSearchMethod: action.payload,
            };

        default:
            return state;
    }
};

const VehicleSearchContext = createContext<VehicleSearchContextType | undefined>(undefined);

export { VehicleSearchContext };

export const VehicleSearchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(vehicleSearchReducer, initialState);

    const contextValue: VehicleSearchContextType = {
        ...state,
        selectVehicle: (vehicle) => dispatch({ type: 'SELECT_VEHICLE', payload: vehicle }),
        toggleFavorite: (vehicle) => dispatch({ type: 'TOGGLE_FAVORITE', payload: vehicle }),
        clearSelection: () => dispatch({ type: 'CLEAR_SELECTION' }),
        removeFavorite: (vehicleId) => dispatch({ type: 'REMOVE_FAVORITE', payload: vehicleId }),
        addRecentSearch: (query) => dispatch({ type: 'ADD_RECENT_SEARCH', payload: query }),
        setActiveSearchMethod: (method) =>
            dispatch({ type: 'SET_ACTIVE_SEARCH_METHOD', payload: method }),
    };

    return (
        <VehicleSearchContext.Provider value={contextValue}>
            {children}
        </VehicleSearchContext.Provider>
    );
};
